import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "../../components/ui/sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";

const emptyForm = { slug: "", title: "", category: "", excerpt: "", content: "", cover_image: "", author: "Orion Services Team", read_time: "5 min read", meta_description: "" };

export function BlogManager({ onChange }) {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/blog").then((res) => setPosts(res.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm(p); setOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.put(`/blog/${editing.id}`, form);
        toast.success("Article updated");
      } else {
        await api.post("/blog", form);
        toast.success("Article published");
      }
      setOpen(false);
      load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/blog/${id}`);
      toast.success("Article deleted");
      load();
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-blog-manager">
      <div className="flex justify-end mb-4">
        <Button onClick={openAdd} data-testid="admin-add-blog-btn" className="bg-amber-light text-obsidian hover:bg-white">
          <Plus className="h-4 w-4 mr-2" /> New Article
        </Button>
      </div>

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center justify-between border border-amber-500/10 rounded-md p-4 bg-carbon" data-testid={`admin-blog-row-${p.slug}`}>
            <div>
              <p className="text-white font-medium">{p.title}</p>
              <p className="text-xs text-zinc-500 font-mono mt-1">{p.slug} &middot; {p.category}</p>
            </div>
            <div className="flex gap-2">
              <Button size="icon" variant="outline" onClick={() => openEdit(p)} data-testid={`admin-edit-blog-btn-${p.slug}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="icon" variant="destructive" data-testid={`admin-delete-blog-btn-${p.slug}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this article?</AlertDialogTitle>
                    <AlertDialogDescription>This will permanently remove "{p.title}".</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDelete(p.id)}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl !bg-carbon border-amber-500/15 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{editing ? "Edit Article" : "New Article"}</DialogTitle>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-zinc-400">Slug</Label>
              <Input data-testid="admin-blog-form-slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Category</Label>
              <Input data-testid="admin-blog-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Title</Label>
              <Input data-testid="admin-blog-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Cover Image URL</Label>
              <Input data-testid="admin-blog-form-cover" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Author</Label>
              <Input data-testid="admin-blog-form-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Read Time</Label>
              <Input data-testid="admin-blog-form-readtime" value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Meta Description (SEO)</Label>
              <Input data-testid="admin-blog-form-meta" value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Excerpt</Label>
              <Textarea data-testid="admin-blog-form-excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs text-zinc-400">Content (paragraphs separated by blank lines)</Label>
              <Textarea data-testid="admin-blog-form-content" rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSave} data-testid="admin-save-blog-btn" className="bg-amber-light text-obsidian hover:bg-white">
              {editing ? "Save Changes" : "Publish Article"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
